import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostBinding,
  Input,
  OnInit,
  Optional,
  Output,
  ViewChild
} from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormGroupDirective, NG_VALUE_ACCESSOR } from '@angular/forms';
import { createTextMaskInputElement } from 'text-mask-core/dist/textMaskCore';
import { isTouchDevice } from '../utils/device';
import { IconTypes } from '../svg-icon/icon-types';
import { LocalizationService } from '../localization/localization.service';

export class TextMaskConfig {
  mask: Array<string | RegExp> | ((raw: string) => Array<string | RegExp>) | false;
  guide?: boolean;
  placeholderChar?: string;
  pipe?: (conformedValue: string, config: TextMaskConfig) => false | string | object;
  keepCharPositions?: boolean;
  showMask?: boolean;
}

@Component({
  selector: 'mtg-text-field',
  templateUrl: './text-field.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TextFieldComponent),
      multi: true
    }
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TextFieldComponent implements OnInit, ControlValueAccessor, AfterViewInit {
  private control: AbstractControl;
  private onChangeFunc: any;
  private onTouchedFunc: any;
  private textMaskInputElement;
  private inputElement: HTMLInputElement|HTMLTextAreaElement;

  @ViewChild('input', {read: ElementRef})
  input: ElementRef;
  @ViewChild('textarea', {read: ElementRef})
  textarea: ElementRef;

  @Input()
  type: string;
  @Input()
  placeholder: string;
  @Input()
  label: string;
  @Input()
  disabled: boolean;
  @Input()
  required: boolean;
  @Input()
  trailingIcon: IconTypes;
  @Input()
  leadingIcon: IconTypes;
  @Input()
  min: number;
  @Input()
  pattern: string;
  @Input()
  formControlName: string;
  @Input()
  formControl: AbstractControl;
  @Input()
  value: any;
  @Input()
  textMask: TextMaskConfig;
  @Input()
  prefix: string;
  @Input()
  trim: boolean;
  @Input()
  errorVisible = true;
  @Input()
  size: 'xs' | 'sm' | 'md' = 'md';
  @Input()
  withClearButton: boolean;

  @Output()
  change = new EventEmitter<any>();
  @Output()
  focus = new EventEmitter<any>();
  @Output()
  blur = new EventEmitter<any>();
  @Output()
  valueChange = new EventEmitter<any>();
  @Output()
  keyDownEvent = new EventEmitter<any>();

  isFocused: boolean;
  hasErrorsOnFocus: boolean;
  showPassword: boolean;

  @HostBinding('class')
  get className() {
    const value = this.inputElement && this.inputElement.value || this.value || '';
    const classList = ['text-field'];
    const control = this.control || this.formControl;
    if (!this.isFocused || this.hasErrorsOnFocus) {
      if (control && control.touched && !control.valid && control.enabled) {
        classList.push('error');
      }
    }
    if (value.toString().length > 0) {
      classList.push('filled');
    }
    if (this.trailingIcon || this.withClearButton) {
      classList.push('with-trailing-icon');
    }
    if (this.leadingIcon) {
      classList.push('with-leading-icon');
    }
    if (this.placeholder) {
      classList.push('with-placeholder');
    }
    if (this.prefix) {
      classList.push('with-prefix');
    }
    if (!this.label) {
      classList.push('without-label');
    }
    if (this.isFocused) {
      classList.push('focused');
    }
    if (this.size === 'sm') {
      classList.push('text-field--size-sm');
    } else if (this.size === 'xs') {
      classList.push('text-field--size-xs');
    } else {
      classList.push('text-field--size-md');
    }
    return classList.join(' ');
  }

  constructor(
    @Optional() private parentFormGroup: FormGroupDirective,
    private localizationService: LocalizationService,
  ) {}

  ngOnInit() {
    if (this.parentFormGroup) {
      this.control = this.formControl || this.parentFormGroup.control.get(this.formControlName);
    }
  }

  ngAfterViewInit(): void {
    if (this.input) {
      this.inputElement = this.input.nativeElement;
    } else if (this.textarea) {
      this.inputElement = this.textarea.nativeElement;
    }
    if (this.inputElement && this.value) {
      this.inputElement.value = this.value;
    }
    if (this.textMask) {
      this.setupMask();
    }
  }

  clear(): void {
    this.changeValue('');
    if (this.textMaskInputElement !== undefined) {
      this.textMaskInputElement.update('');
    } else if (this.inputElement) {
      this.inputElement.value = '';
    }
  }

  writeValue(value: any): void {
    this.value = value;

    if (this.textMaskInputElement !== undefined) {
      this.textMaskInputElement.update(value);
    } else if (this.inputElement) {
      this.inputElement.value = value;
    }
  }

  registerOnChange(fn: any): void {
    this.onChangeFunc = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedFunc = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  changeValue(value: any, updateViewValue = false) {
    if (this.textMaskInputElement !== undefined) {
      this.textMaskInputElement.update(value);
      value = this.inputElement.value;
    } else if (updateViewValue) {
      this.inputElement.value = value;
    }
    if (this.type === 'number' && value.length > 0) {
      value = +value;
    }
    if (this.type !== 'number' && this.trim) {
      if (value.trim().length !== value.length) {
        value = value.trim();
        this.inputElement.value = value;
      }
    }
    this.change.emit(value);
    this.valueChange.emit(value);
    if (this.onChangeFunc) {
      this.onChangeFunc(value);
    }
    this.value = value;
  }

  onFocus($event: any): void {
    this.isFocused = true;
    this.hasErrorsOnFocus = this.getErrors().length > 0;
    this.focus.emit($event);
    if (isTouchDevice()) {
      document.body.classList.add('input-focused');
    }
  }

  onBlur($event: any): void {
    this.isFocused = false;
    if (this.onTouchedFunc) {
      this.onTouchedFunc();
    }
    if (isTouchDevice()) {
      document.body.classList.remove('input-focused');
    }
    this.blur.emit($event);
  }

  onKeyDown($event: any): void {
    this.keyDownEvent.emit($event);
  }

  getErrors() {
    const control = (this.control || this.formControl);
    let errors = {};
    if (control && control.touched && control.errors) {
      errors = control.errors;
    }
    return Object.keys(errors)
      .filter(k => errors[k])
      .reduce((acc, value) => {
        let error = this.localizationService.formErrorLocalize(value);
        if (!error) {
          return acc;
        }
        if (typeof errors[value] === 'object') {
          for (const errorKey of Object.keys(errors[value])) {
            error = error.replace(`{${errorKey}}`, errors[value][errorKey]);
          }
        }
        acc.push(error);
        return acc;
      }, []);
  }

  private setupMask() {
    if (this.inputElement) {
      this.textMaskInputElement = createTextMaskInputElement(
        Object.assign({inputElement: this.inputElement}, this.textMaskConfig)
      );
    }
  }

  get textMaskConfig() {
    const defaultConfig = {
      mask: [],
      guide: true,
      placeholderChar: '_',
      pipe: undefined,
      keepCharPositions: false,
    };
    return Object.assign(defaultConfig, this.textMask);
  }
}
